import React, { ReactNode, Suspense, createContext, useContext, useState, useTransition } from 'react';
import { useLazyLoadQuery } from 'react-relay';
import { GraphQLTaggedNode, OperationType } from 'relay-runtime';
import styled from 'styled-components';
import { BasicButton } from 'src/components/widgets/buttons';
import { Section } from 'src/components/widgets/section';
import { SkeletonBlock } from 'src/components/widgets/skeleton';
import Stack from 'src/components/widgets/stack/stack';
import { Row, Table, TableContentLoader } from 'src/components/widgets/table';
import { T } from 'src/shared/helpers';
import { dashboard } from '../dashboard/dashboardTheme';

export const number = (value: number | null | undefined) =>
    value == null ? '—' : new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(value);
export const money = (value: number | null | undefined, currency = 'USD') =>
    value == null ? '—' : new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(value);
export const date = (value: string | null | undefined) => (value ? new Date(value).toLocaleString(undefined, { timeZone: 'Europe/Rome' }) : '—');
export const Note = styled.p`
    color: ${(p) => p.theme.app.primary};
    margin: 0;
    font-size: 13px;
    line-height: 1.6;
`;
export const MetricGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 16px;
`;
const MetricValue = styled.strong`
    font-size: 25px;
`;
export const Metric = ({ label, value, detail, contained = true }: { label: string; value: ReactNode; detail?: string; contained?: boolean }) => {
    const content = (
        <Stack $gap='8px'>
            <Note>{label}</Note>
            <MetricValue>{value}</MetricValue>
            {detail && <Note>{detail}</Note>}
        </Stack>
    );
    return contained ? <Section>{content}</Section> : content;
};
export const OverviewLayout = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1.5fr) minmax(360px, 1fr);
    gap: 16px;
    > * {
        min-width: 0;
    }
    @media (max-width: 1050px) {
        grid-template-columns: minmax(0, 1fr);
    }
`;
export const OverviewMetrics = styled(MetricGrid)`
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 20px 24px;
    > :first-child {
        grid-column: 1 / -1;
    }
    strong {
        overflow-wrap: anywhere;
    }
`;
export const CostSummary = styled.dl<{ $margin?: number | null }>`
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 16px 24px;
    margin: 0;
    padding-top: 20px;
    border-top: 1px solid ${dashboard.line};
    dt,
    dd {
        margin: 0;
        align-self: center;
    }
    dd {
        text-align: right;
        font-weight: 600;
    }
    > :nth-child(2) {
        color: ${dashboard.ok};
    }
    > :nth-child(4) {
        color: ${dashboard.bad};
    }
    > :nth-child(6) {
        color: ${(p) => (p.$margin == null || p.$margin === 0 ? 'inherit' : p.$margin < 0 ? dashboard.bad : dashboard.ok)};
        font-weight: 700;
    }
`;
export const ReportOverviewSkeleton = () => (
    <OverviewLayout aria-busy='true' aria-label={T._('Loading report', 'Admin')}>
        <Section title={T._('Daily credits', 'Admin')}>
            <SkeletonBlock $height='300px' $width='100%' />
        </Section>
        <Section title={T._('Usage summary', 'Admin')}>
            <Stack $gap='20px'>
                <OverviewMetrics>
                    {Array.from({ length: 3 }, (_, index) => (
                        <Stack key={index} $gap='8px'>
                            <SkeletonBlock $height='16px' $width='85%' />
                            <SkeletonBlock $height='30px' $width='65%' />
                        </Stack>
                    ))}
                </OverviewMetrics>
                <CostSummary>
                    {Array.from({ length: 3 }, (_, index) => (
                        <React.Fragment key={index}>
                            <dt>
                                <SkeletonBlock $height='18px' $width='120px' />
                            </dt>
                            <dd>
                                <SkeletonBlock $height='20px' $width='80px' />
                            </dd>
                        </React.Fragment>
                    ))}
                </CostSummary>
            </Stack>
        </Section>
    </OverviewLayout>
);
const Scroll = styled.div`
    overflow-x: auto;
    min-width: 0;
`;
const CellContent = styled.span`
    min-width: 0;
    overflow-wrap: anywhere;
`;
export function DataTable({ headers, rows, label, pending = false }: { headers: string[]; rows: ReactNode[][]; label: string; pending?: boolean }) {
    return rows.length ? (
        <Stack $gap='12px'>
            <Scroll tabIndex={0} role='region' aria-label={label}>
                <Table headings={headers.map((header) => [header, 'minmax(max-content, 1fr)'])} compact $grayout={pending}>
                    {rows.map((row, i) => (
                        <Row
                            key={i}
                            cells={row.map((value, j) => (
                                <CellContent key={j}>{value}</CellContent>
                            ))}
                        />
                    ))}
                </Table>
            </Scroll>
        </Stack>
    ) : (
        <Note>{T._('No data for the selected filters.', 'Admin')}</Note>
    );
}
export function useReportPage() {
    const [skip, setSkip] = useState(0);
    const [pending, startTransition] = useTransition();
    const changePage = (nextSkip: number) => startTransition(() => setSkip(nextSkip));
    return [skip, changePage, pending] as const;
}
export const Pager = ({ skip, total, onChange, pending = false }: { skip: number; total: number; onChange: (skip: number) => void; pending?: boolean }) => (
    <Stack $horizontal $gap='12px' style={{ alignItems: 'center', flexWrap: 'wrap' }}>
        <BasicButton disabled={pending || skip === 0} onClick={() => onChange(Math.max(0, skip - 50))}>
            {T._('Previous', 'Admin')}
        </BasicButton>
        <Note>{total ? `${number(skip + 1)}–${number(Math.min(skip + 50, total))} / ${number(total)}` : '0 / 0'}</Note>
        <BasicButton disabled={pending || skip + 50 >= total || skip + 50 > 1000000} onClick={() => onChange(skip + 50)}>
            {T._('Next', 'Admin')}
        </BasicButton>
    </Stack>
);
export function useReport<Q extends OperationType>(query: GraphQLTaggedNode, variables: Q['variables']) {
    const attempt = useContext(ReportAttempt);
    const data = useLazyLoadQuery<Q>(query, variables, { fetchPolicy: 'network-only', fetchKey: attempt });
    return data;
}
const ReportAttempt = createContext(0);
export class ReportBoundary extends React.Component<{ children: ReactNode; fallback?: ReactNode }, { failed: boolean; attempt: number }> {
    state = { failed: false, attempt: 0 };
    static getDerivedStateFromError() {
        return { failed: true };
    }
    retry = () => this.setState((state) => ({ failed: false, attempt: state.attempt + 1 }));
    render() {
        if (this.state.failed) return <ReportStatus error={T._('Unable to load this section. Please retry.', 'Admin')} retry={this.retry} />;
        return (
            <ReportAttempt.Provider value={this.state.attempt}>
                <Suspense fallback={this.props.fallback ?? <ReportStatus retry={this.retry} />}>{this.props.children}</Suspense>
            </ReportAttempt.Provider>
        );
    }
}
export const ReportStatus = ({ error, retry }: { error?: string; retry: () => void }) =>
    error ? (
        <Section>
            <Stack $gap='12px'>
                <p role='alert'>{error}</p>
                <BasicButton onClick={retry}>{T._('Retry', 'Admin')}</BasicButton>
            </Stack>
        </Section>
    ) : (
        <Section>
            <Stack $gap='16px' aria-busy='true' aria-label={T._('Loading report', 'Admin')}>
                <SkeletonBlock $height='50px' $width='100%' />
                <TableContentLoader rows={8} />
            </Stack>
        </Section>
    );
